import { useEffect } from 'react';

export const useModalEvent = (
  modalId: string,
  modalContentRef: React.RefObject<HTMLDivElement>,
  onEscapePress: () => void,
  onOpen: () => void
) => {
  useEffect(() => {
    let mouseDownTarget: Element | null = null;

    const handleClick = (event: MouseEvent) => {
      // Only handle left clicks (button 0)
      if (event.button !== 0) return;

      // Check if this is a complete click gesture that started and ended outside
      const clickOutside =
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target as Node);

      const mouseDownOutside =
        mouseDownTarget &&
        modalContentRef.current &&
        !modalContentRef.current.contains(mouseDownTarget);

      // If mousedown started inside, prevent the global modal system from closing
      // if it is only the modal backdrop that is clicked
      if (!mouseDownOutside && clickOutside) {
        const target = event.target as Element;
        const isModalBackdrop = target.closest(
          `#${modalId}.modal[data-modal="true"] [data-backdrop="true"]`
        );

        // Only prevent default if it's the modal backdrop
        if (isModalBackdrop) {
          event.stopPropagation();
          event.preventDefault();
        }
      }

      // Reset the target
      mouseDownTarget = null;
    };

    const handleMouseDown = (event: MouseEvent) => {
      // Only handle left clicks (button 0)
      if (event.button !== 0) return;

      // Store the target where mousedown occurred
      mouseDownTarget = event.target as Element;
    };

    const handleEscapePress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onEscapePress?.();
      }
    };

    // Use capture phase to intercept events before the global modal system
    document.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleEscapePress);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown, true);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleEscapePress);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Simple solution to detect when modal is open
  useEffect(() => {
    const modalElement = document.getElementById(modalId);
    if (!modalElement || !onOpen) return undefined;

    const handleModalShown = () => {
      onOpen();
    };

    modalElement.addEventListener('shown', handleModalShown);

    return () => {
      modalElement.removeEventListener('shown', handleModalShown);
    };
  }, [modalId, onOpen]);
};
